import React from "react";
import NextImage from "next/image";
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";

import MetaTitle from "./MetaTitle";
import Title from "./Title";
import Text from "./Text";
import DynamicLink from "./DynamicLink";
import ButtonGroup from "./ButtonGroup";
import ImageLabel from "./ImageLabel";
import { colors } from "utils/styles";

export default function AccessKnowledge({ data }) {
	const hashLink = "accessKnowledge";
	
	// Get section configuration from Contentful
	const sectionConfig = data?.fields?.sectionConfig || {};
	const knowledgeItems = data?.fields?.jsonData?.data || [];
	
	// Fallback to default configuration if no data is found
	const config = {
		metaTitle: sectionConfig.metaTitle || "RESOURCES & PUBLICATIONS",
		title: sectionConfig.title || "Access Knowledge",
		description: sectionConfig.description || "Read, watch, listen. Dive into our journals, newsletters, books, webinars, and podcasts.",
		...sectionConfig
	};
	
	// Fallback to default items if no data is found
	const defaultItems = [
		{
			id: "population-health-course",
			type: "large",
			image: "/img/ALC25d2_afternoon_46.jpg",
			alt: "Conference crowd networking",
			tags: [
				{ text: "ON DEMAND", type: "on-demand" },
				{ text: "SELF-STUDY", type: "self-study" }
			],
			title: "A Journey Through Population Health",
			description: "Population health can be defined as the application of public health principles to health care...",
			link: "/education/course/population-health?v=42540108251316",
			buttonText: "LEARN MORE",
			credits: "Credits 14 CME"
		},
		{
			id: "journals",
			type: "small",
			image: "/img/Journals.jpg",
			alt: "Physician Leadership Journal",
			title: "Journals",
			description: "Discover peer-reviewed insights on physician leadership and healthcare management.",
			link: "/articles",
			buttonText: "LEARN MORE"
		},
		{
			id: "leadership-library",
			type: "small",
			image: "/img/Leadership Library (1).jpg",
			alt: "Physician Leadership White Paper",
			title: "Leadership Library",
			description: "Stay informed with expert-driven newsletters, and white papers.",
			link: "/articles",
			buttonText: "LEARN MORE"
		},
		{
			id: "bookstore",
			type: "small",
			image: "/img/Bookstore (1).jpg",
			alt: "Stack of healthcare leadership books",
			title: "Bookstore",
			description: "Essential reading for every stage of your journey.",
			link: "/publications/books",
			buttonText: "LEARN MORE"
		},
		{
			id: "podcast",
			type: "small",
			image: "/img/Sound Practice Podcast.jpg",
			alt: "Sound Practice Podcast Logo",
			title: "Physician Leadership Podcast",
			description: "Fresh audio conversations with industry experts",
			link: "https://www.soundpractice.com/",
			buttonText: "LEARN MORE"
		}
	];
	
	const items = knowledgeItems.length > 0 ? knowledgeItems : defaultItems;
	
	// Separate large and small items
	const largeItems = items.filter(item => item.type === "large");
	const smallItems = items.filter(item => item.type === "small");

	return (
		<section id={hashLink} className="access-knowledge-section">
			<div className="inner-wrapper">
				<Container fluid className="px-5">
					<div className="header-section text-start mb-5">
						<MetaTitle textAlign="start" text={config.metaTitle} />
						<Title
							className="w-100"
							textJustify="start"
							textAlign="start"
							element="h2"
							text={config.title}
							marginBottom={1}
						/>
						<Text
							text={config.description}
							textAlign="start"
							size="16"
							weight="100"
							textJustify="start"
							className="mb-0 text-muted"
						/>
					</div>

					<div className="g-0 w-100 d-flex flex-column flex-md-row equal-height-row px-md-4">
						{largeItems.length > 0 && (
							<Col lg={3} md={12} className="d-flex mb-4 mb-md-0 me-md-4">
								{largeItems.map((item) => (
									<div key={item.id} className="knowledge-card large-card">
										<div className="card-image" style={{ maxHeight: "160px", overflow: "hidden", marginBottom: "0.75rem" }}>
											<NextImage
												src={item.image}
												alt={item.alt}
												width={300}
												height={160}
												style={{ width: "100%", height: "100%", objectFit: "cover" }}
											/>
										</div>
										<div className="card-content pt-0">
											{item.tags && (
												<div className="tags">
													{item.tags.map((tag, index) => (
														<span key={index} className={`tag ${tag.type}`}>
															{tag.text}
														</span>
													))}
												</div>
											)}
											<Title element="h3" text={item.title} className="mb-0 pb-0" />
											<Text
												text={item.description}
												size="13"
												weight="100"
											/>
											<div className="card-footer">
												<DynamicLink href={item.link} className="learn-more-link" underlineStyle={true}>
													{item.buttonText}
												</DynamicLink>
												{item.credits && <span className="credits">{item.credits}</span>}
											</div>
										</div>
									</div>
								))}
							</Col>
						)}

						{/* Right Side - Grid of Small Cards */}
						{smallItems.length > 0 && (
							<Col lg={largeItems.length > 0 ? 9 : 12} md={12} className="d-flex">
								<div className="small-cards-grid">
									{smallItems.map((item) => (
										<div key={item.id} className="knowledge-card small-card">
											<div className="small-card-content">
												<div className="card-image-small">
													<NextImage
														src={item.image}
														alt={item.alt}
														width={100}
														height={100}
														style={{ width: "100%", height: "100%", objectFit: "cover" }}
													/>
												</div>
												<div className="card-content">
													<Title element="h4" text={item.title} marginBottom={1} fontFamily="Athelas" />
													<Text
														text={item.description}
														size="12"
														className="description"
													/>
													<DynamicLink href={item.link} className="learn-more-link" underlineStyle={true}>
														{item.buttonText}
													</DynamicLink>
												</div>
											</div>
										</div>
									))}
								</div>
							</Col>
						)}
					</div>
				</Container>
			</div>

			<style jsx="true">{`
				.access-knowledge-section {
					background: #f8f9fa;
					padding: 2rem 0;
				}

				.inner-wrapper {
					background: #f8f9fa;
				}

				.equal-height-row {
					align-items: stretch;
				}

				.small-cards-grid {
					display: grid;
					grid-template-columns: repeat(2, 1fr);
					gap: 1rem;
					width: 100%;
				}

				.header-section {
					margin-bottom: 4rem;
				}

				.subtitle {
					font-size: 1.1rem;
					color: #666;
					margin-top: 1rem;
					max-width: 600px;
					margin-left: auto;
					margin-right: auto;
				}

				.knowledge-card {
					background: white;
					border-radius: 8px;
					box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
					height: 100%;
					display: flex;
					flex-direction: column;
					overflow: hidden;
					transition: transform 0.2s ease, box-shadow 0.2s ease;
				}

				.knowledge-card:hover {
					transform: translateY(-2px);
					box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
				}

				/* Large Card Styles */
				.large-card {
					height: 100%;
				}

				.large-card .card-image {
					height: 160px;
				}

				.large-card .card-content {
					padding: 1.25rem;
					flex: 1;
				}

				.large-card .tags {
					display: flex;
					gap: 0.75rem;
					margin-bottom: 0.75rem;
				}

				.large-card .description {
					font-size: 1rem;
					margin: 1rem 0;
				}

				/* Small Card Styles */
				.small-card {
					height: 100%;
				}

				.small-card-content {
					display: flex;
					height: 100%;
				}

				.card-image-small {
					width: 180px;
					height: 100%;
					flex-shrink: 0;
					overflow: hidden;
				}

				.small-card .card-content {
					padding: 1rem;
					flex: 1;
					display: flex;
					flex-direction: column;
					justify-content: center;
					align-items: flex-start;
				}

				.small-card .description {
					font-size: 0.9rem;
					margin: 0.5rem 0;
					line-height: 1.3;
				}

				.card-image {
					position: relative;
					overflow: hidden;
					flex-shrink: 0;
				}

				.tag {
					padding: 0.375rem 0.75rem;
					border-radius: 16px;
					font-size: 0.65rem;
					font-weight: 600;
					text-transform: uppercase;
					letter-spacing: 0.5px;
				}

				.tag.on-demand {
					background: #D4A574;
					color: white;
				}

				.tag.self-study {
					background: #e9ecef;
					color: #495057;
				}

				.card-logos {
					height: 100%;
					background: white;
					display: flex;
					flex-direction: column;
					align-items: center;
					justify-content: center;
					padding: 1rem;
					gap: 0.75rem;
				}

				.logo-item {
					text-align: center;
				}

				.logo-title {
					font-size: 1.25rem;
					font-weight: bold;
					color: #2c5aa0;
					margin-bottom: 0.25rem;
				}

				.logo-subtitle {
					font-size: 0.6rem;
					color: #666;
					text-transform: uppercase;
					letter-spacing: 0.5px;
					line-height: 1.2;
				}

				.logo-wave {
					font-size: 1.25rem;
					color: #2c5aa0;
					font-weight: bold;
				}

				.podcast-logo {
					text-align: center;
				}

				.logo-letters {
					font-size: 2rem;
					font-weight: bold;
					color: #2c5aa0;
					margin-bottom: 0.25rem;
				}

				.logo-text {
					font-size: 0.8rem;
					color: #666;
					font-weight: 500;
				}

				.card-content {
					display: flex;
					flex-direction: column;
				}

				.description {
					color: #666;
					line-height: 1.5;
				}

				.card-footer {
					display: flex;
					justify-content: space-between;
					align-items: center;
					margin-top: auto;
				}

				.learn-more-link {
					color: #2c5aa0;
					text-decoration: none;
					font-weight: 600;
					font-size: 0.75rem;
					text-transform: uppercase;
					letter-spacing: 0.5px;
					transition: color 0.2s ease;
					margin-top: auto;
				}

				.small-card .learn-more-link {
					font-size: 0.7rem;
				}

				.learn-more-link:hover {
					color: #1e3a5f;
					text-decoration: underline;
				}

				.credits {
					font-size: 0.8rem;
					color: #666;
					font-weight: 500;
				}

				@media (max-width: 992px) {
					.large-card .card-image {
						height: 140px;
					}
					
					.card-image-small {
						width: 150px;
					}
					
					.small-cards-grid {
						grid-template-columns: repeat(2, 1fr);
					}
				}

				@media (max-width: 768px) {
					.header-section {
						margin-bottom: 2rem;
					}
					
					.access-knowledge-section {
						padding: 2rem 0;
					}

					.large-card .card-image {
						height: 200px;
					}

					.card-image-small {
						width: 140px;
					}

					.large-card .card-content {
						padding: 1.5rem;
					}

					.small-card .card-content {
						padding: 1rem;
					}

					.small-card .description {
						font-size: 0.85rem;
					}
					
					.small-cards-grid {
						grid-template-columns: 1fr;
					}
				}
			`}</style>
		</section>
	);
}